import { useLocation } from 'react-router-dom';

// slug正規化関数（強化版）
const normalizeSlug = (slug: string): string => {
  if (!slug) return '';
  
  // 1. 小文字化
  let normalized = slug.toLowerCase();
  
  // 2. 空白除去
  normalized = normalized.trim().replace(/\s+/g, '');
  
  // 3. アンダースコアをハイフンに
  normalized = normalized.replace(/_/g, '-');
  
  // 4. 連続ハイフンを1つに
  normalized = normalized.replace(/-+/g, '-');
  
  // 5. 先頭・末尾のハイフン除去
  normalized = normalized.replace(/^-+|-+$/g, '');
  
  return normalized;
};

// エイリアスマップ（確実にヒットさせるため）
const SLUG_ALIAS_MAP: Record<string, string> = {
  // PETG-GF
  'petggf': 'petg-gf',
  'petg_gf': 'petg-gf',
  'petg-gf': 'petg-gf',
  
  // ABS-CF
  'abscf': 'abs-cf',
  'abs_cf': 'abs-cf',
  'abs-cf': 'abs-cf',
  
  // ABS-GF
  'absgf': 'abs-gf',
  'abs_gf': 'abs-gf',
  'abs-gf': 'abs-gf',
  
  // ASA-CF
  'asacf': 'asa-cf',
  'asa_cf': 'asa-cf',
  'asa-cf': 'asa-cf',
  
  // ASA-GF
  'asagf': 'asa-gf',
  'asa_gf': 'asa-gf',
  'asa-gf': 'asa-gf',
  
  // PC
  'pc': 'pc',
  
  // PC-CF
  'pccf': 'pc-cf',
  'pc_cf': 'pc-cf',
  'pc-cf': 'pc-cf',
  
  // PC-GF
  'pcgf': 'pc-gf',
  'pc_gf': 'pc-gf',
  'pc-gf': 'pc-gf',
  
  // PA
  'pa': 'pa',
  
  // PA-CF
  'pacf': 'pa-cf',
  'pa_cf': 'pa-cf',
  'pa-cf': 'pa-cf',
  
  // PA-GF
  'pagf': 'pa-gf',
  'pa_gf': 'pa-gf',
  'pa-gf': 'pa-gf',
};

export const useFilamentText = (slug?: string) => {
  const location = useLocation();
  const isEnglish = location.pathname === '/en' || location.pathname.startsWith('/en/');

  // slugが渡された場合は正規化してエイリアス適用
  let normalizedSlug = '';
  if (slug) {
    const normalized = normalizeSlug(slug);
    normalizedSlug = SLUG_ALIAS_MAP[normalized] || normalized;
  }

  const TEXT_JA = {
    // 共通セクション
    features: '特徴',
    basicInfo: '基本情報',
    recommendedSettings: '推奨設定',
    bendingStrength: '曲げ強度',
    derivatives: '派生・バリエーション',
    useCases: '使用例',
    precautions: '注意事項',
    
    // 基本情報項目
    classification: '分類：',
    beginner: '初級',
    intermediate: '中級',
    advanced: '上級',
    baseResin: 'ベース樹脂：',
    meltingPoint: '融点：',
    density: '密度：',
    shrinkageRate: '収縮率：',
    heatResistance: '耐熱温度：',
    
    // 推奨設定項目
    item: '項目',
    recommendedValue: '推奨値',
    notes: '備考',
    nozzleTemp: 'ノズル温度',
    bedTemp: 'ベッド温度',
    fanSetting: 'ファン設定',
    printSpeed: '印刷速度',
    nozzleDiameter: 'ノズル径推奨',
    caution: '注意点',
    
    // 曲げ強度項目
    bendingStrengthValue: '曲げ強度',
    flexuralModulus: '曲げ弾性率',
    testMethod: '試験方法',
    threePointBending: '3点曲げ試験',
    details: '詳細',
    
    // 材料別の説明文
    materials: {
      'pla-cf': {
        features: [
          'カーボン繊維入り。軽量高強度。硬化ノズル推奨。',
          '優れた剛性と寸法安定性',
          'マット仕上げで高級感'
        ],
        baseResin: 'ポリ乳酸 + カーボン繊維',
        derivatives: 'PLAをベースに、カーボン繊維を配合した高強度フィラメント。軽量で剛性が高く、機能部品に適しています。',
        suitableUses: ['ドローン部品', '機能部品', '治具・工具', '軽量構造物'],
        proscons: { pros: '軽量、高剛性、寸法安定性', cons: 'ノズル摩耗、脆い、高価' },
        precautions: ['硬化ノズル（ステンレス・ルビー）必須です。', '層間接着が弱いため、応力方向に注意してください。', '通常のPLAより脆いため、衝撃に弱いです。']
      },
      'pla-silk': {
        features: [
          '光沢重視。美しい仕上がりが特徴。装飾品向け。',
          '独特の光沢感と質感',
          '積層痕が目立ちにくい'
        ],
        baseResin: 'ポリ乳酸 + 光沢添加剤',
        derivatives: 'PLAに光沢添加剤を配合したフィラメント。美しい仕上がりで装飾品や芸術作品に最適です。',
        suitableUses: ['装飾品', 'フィギュア', 'アート作品', 'ギフト・記念品'],
        proscons: { pros: '美しい光沢、豊富な色、印刷容易', cons: '強度低い、機能部品不向き、高価' },
        precautions: ['機械的強度が低いため、機能部品には不向きです。', '印刷速度を下げると光沢が向上します。', '層間接着が弱いため、衝撃に注意してください。']
      },
      'matte-pla': {
        features: [
          'マット仕上げ。積層痕が目立ちにくい高級感。',
          '落ち着いた質感',
          '指紋が目立ちにくい'
        ],
        baseResin: 'ポリ乳酸 + マット添加剤',
        derivatives: 'PLAにマット添加剤を配合したフィラメント。積層痕が目立ちにくく、高級感のある仕上がりが特徴です。',
        suitableUses: ['プロトタイプ', 'インテリア小物', '建築模型', '展示用モデル'],
        proscons: { pros: '高級感、積層痕目立たない、印刷容易', cons: '強度やや低い、色の選択肢少ない' },
        precautions: ['通常のPLAよりやや脆いため、機能部品には注意が必要です。', '印刷温度を若干高めに設定すると仕上がりが向上します。']
      },
      'pla-pro': {
        features: [
          '高強度・高靭性。機能部品にも使用可能。',
          '優れた層間接着',
          '耐衝撃性向上'
        ],
        baseResin: 'ポリ乳酸（改良型）',
        derivatives: 'PLAの機械的特性を大幅に改良したフィラメント。高強度・高靭性で機能部品にも使用できます。',
        suitableUses: ['機能部品', '治具・工具', 'スナップフィット', '耐久性が必要な部品'],
        proscons: { pros: '高強度、高靭性、印刷容易、PLA並の扱いやすさ', cons: 'やや高価、耐熱性は通常PLA並' },
        precautions: ['通常のPLAより若干高温での印刷を推奨します。', '耐熱性は通常のPLAと同程度のため、高温環境では使用できません。']
      },
      'ht-pla': {
        features: [
          '高耐熱PLA。アニーリングで100°C以上に対応。',
          '結晶化による耐熱性向上',
          'PLAの扱いやすさを維持'
        ],
        baseResin: 'ポリ乳酸（高結晶性）',
        derivatives: 'アニーリング（熱処理）により耐熱性を大幅に向上させたPLA。100°C以上の環境でも使用可能になります。',
        suitableUses: ['耐熱部品', '自動車内装', '食器・カトラリー', '高温環境部品'],
        proscons: { pros: '高耐熱（アニーリング後）、印刷容易、食品接触可', cons: 'アニーリング必須、収縮大きい、高価' },
        precautions: ['アニーリング（熱処理）が必須です。オーブンで80-110°Cで1-2時間加熱してください。', 'アニーリング時に約10-15%収縮するため、寸法を考慮して設計してください。', 'アニーリング前は通常のPLAと同程度の耐熱性です。']
      },
      'petg-cf': {
        features: [
          'カーボン繊維強化PETG。高剛性と耐薬品性。',
          '優れた寸法安定性',
          'マット仕上げ'
        ],
        baseResin: 'ポリエチレンテレフタレートグリコール + カーボン繊維',
        derivatives: 'PETGにカーボン繊維を配合した高性能フィラメント。剛性と耐薬品性を兼ね備えています。',
        suitableUses: ['機能部品', '治具・工具', '耐薬品性部品', '構造部品'],
        proscons: { pros: '高剛性、耐薬品性、寸法安定性', cons: 'ノズル摩耗、脆い、高価' },
        precautions: ['硬化ノズル必須です。', '層間接着が弱いため、応力方向に注意してください。']
      },
      'petg-gf': {
        features: [
          'ガラス繊維強化PETG。高強度と寸法安定性。',
          '優れた耐熱性',
          '工業用途に最適'
        ],
        baseResin: 'ポリエチレンテレフタレートグリコール + ガラス繊維',
        derivatives: 'PETGにガラス繊維を配合した高強度フィラメント。寸法安定性と耐熱性に優れています。',
        suitableUses: ['工業部品', '治具・工具', '耐熱部品', '構造部品'],
        proscons: { pros: '高強度、寸法安定性、耐熱性', cons: 'ノズル摩耗、表面粗い、高価' },
        precautions: ['硬化ノズル推奨です。', '表面が粗くなるため、後加工が必要な場合があります。']
      },
      'abs': {
        features: [
          '高い耐衝撃性と靭性',
          '優れた耐熱性（80-100°C）',
          '後加工しやすい',
          '反りやすい、密閉チャンバー推奨',
          '工業用途に適している',
          '機械的強度に優れる'
        ],
        baseResin: 'アクリロニトリルブタジエンスチレン',
        derivatives: 'ABSをベースに、様々な特性を強化した派生フィラメントが開発されています。用途に応じて最適なものを選択してください。',
        suitableUses: ['自動車部品', '電子機器筐体', '工業用治具', '耐熱性が必要な部品', '機械部品', '家電製品外装'],
        proscons: { pros: '高強度、耐熱性、後加工性、靭性', cons: '反りやすい、有害ガス、高温必要' },
        precautions: ['印刷時に有害ガスが発生するため、十分な換気が必要です。', '反りが発生しやすく、密閉チャンバーでの印刷を強く推奨します。', '高温での印刷のため、プリンター性能の確認が必要です。', '初心者には扱いが困難なため、PLA+やPETGから始めることを推奨します。']
      },
      'abs-cf': {
        features: [
          'カーボン繊維強化ABS。軽量高強度。',
          '航空宇宙分野でも使用',
          '優れた剛性'
        ],
        baseResin: 'アクリロニトリルブタジエンスチレン + カーボン繊維',
        derivatives: 'ABSにカーボン繊維を配合した高性能フィラメント。軽量で高強度、航空宇宙分野でも使用されます。',
        suitableUses: ['航空宇宙部品', 'ドローン部品', '高性能治具', '軽量構造物'],
        proscons: { pros: '軽量、高剛性、耐熱性', cons: 'ノズル摩耗、反りやすい、高価' },
        precautions: ['硬化ノズル必須です。', '密閉チャンバーでの印刷を強く推奨します。', '有害ガス発生のため換気必須です。']
      },
      'abs-gf': {
        features: [
          'ガラス繊維強化ABS。寸法安定性向上。',
          '工業部品向け',
          '優れた耐熱性'
        ],
        baseResin: 'アクリロニトリルブタジエンスチレン + ガラス繊維',
        derivatives: 'ABSにガラス繊維を配合した高性能フィラメント。寸法安定性が向上し、工業部品に最適です。',
        suitableUses: ['工業部品', '自動車部品', '治具・工具', '構造部品'],
        proscons: { pros: '寸法安定性、耐熱性、高強度', cons: 'ノズル摩耗、表面粗い、高価' },
        precautions: ['硬化ノズル推奨です。', '密閉チャンバーでの印刷を推奨します。', '有害ガス発生のため換気必須です。']
      },
      'asa': {
        features: [
          '優れた耐候性（UV耐性）',
          '屋外使用に最適',
          'ABSより反りにくい',
          '高い耐熱性',
          '耐薬品性に優れる'
        ],
        baseResin: 'アクリロニトリルスチレンアクリレート',
        derivatives: 'ASAをベースに、様々な特性を強化した派生フィラメントが開発されています。',
        suitableUses: ['屋外用部品', '自動車外装', '看板・サイン', '園芸用品', '建築模型'],
        proscons: { pros: '耐候性、耐UV、耐熱性、ABSより反りにくい', cons: '高温必要、有害ガス、高価' },
        precautions: ['印刷時に有害ガスが発生するため、十分な換気が必要です。', '密閉チャンバーでの印刷を推奨します。', '高温での印刷のため、プリンター性能の確認が必要です。']
      },
      'asa-cf': {
        features: [
          'カーボン繊維強化ASA。耐候性と高剛性。',
          '屋外用高性能部品',
          '優れた寸法安定性'
        ],
        baseResin: 'アクリロニトリルスチレンアクリレート + カーボン繊維',
        derivatives: 'ASAにカーボン繊維を配合した高性能フィラメント。耐候性と剛性を兼ね備えています。',
        suitableUses: ['屋外用構造物', 'ドローン部品', '自動車外装', '高性能治具'],
        proscons: { pros: '耐候性、高剛性、軽量', cons: 'ノズル摩耗、高価、有害ガス' },
        precautions: ['硬化ノズル必須です。', '密閉チャンバーでの印刷を推奨します。', '有害ガス発生のため換気必須です。']
      },
      'asa-gf': {
        features: [
          'ガラス繊維強化ASA。耐候性と高強度。',
          '工業用屋外部品',
          '優れた寸法安定性'
        ],
        baseResin: 'アクリロニトリルスチレンアクリレート + ガラス繊維',
        derivatives: 'ASAにガラス繊維を配合した高性能フィラメント。耐候性と強度を兼ね備えています。',
        suitableUses: ['屋外用工業部品', '自動車部品', '建築部材', '構造部品'],
        proscons: { pros: '耐候性、高強度、寸法安定性', cons: 'ノズル摩耗、表面粗い、高価' },
        precautions: ['硬化ノズル推奨です。', '密閉チャンバーでの印刷を推奨します。', '有害ガス発生のため換気必須です。']
      },
      'pc': {
        features: [
          '非常に高い耐衝撃性',
          '優れた耐熱性（130-140°C）',
          '透明性が高い',
          '高い機械的強度',
          '難易度が高い'
        ],
        baseResin: 'ポリカーボネート',
        derivatives: 'PCをベースに、様々な特性を強化した派生フィラメントが開発されています。',
        suitableUses: ['防護カバー', '安全装備', '耐熱部品', '光学部品', '工業用治具'],
        proscons: { pros: '超高強度、耐熱性、透明性、耐衝撃性', cons: '印刷困難、高温必要、吸湿性、高価' },
        precautions: ['印刷温度が非常に高く（270-310°C）、対応プリンターが限られます。', '吸湿性が非常に高いため、乾燥必須です。', '密閉チャンバーでの印刷を強く推奨します。', '上級者向けの材料です。']
      },
      'pc-cf': {
        features: [
          'カーボン繊維強化PC。超高強度。',
          '航空宇宙グレード',
          '優れた耐熱性と剛性'
        ],
        baseResin: 'ポリカーボネート + カーボン繊維',
        derivatives: 'PCにカーボン繊維を配合した最高性能フィラメント。航空宇宙分野でも使用されます。',
        suitableUses: ['航空宇宙部品', '高性能治具', '耐熱構造物', '防護装備'],
        proscons: { pros: '超高強度、耐熱性、軽量、高剛性', cons: '印刷困難、ノズル摩耗、非常に高価' },
        precautions: ['硬化ノズル必須です。', '印刷温度が非常に高く、対応プリンターが限られます。', '乾燥必須です。', '上級者向けの材料です。']
      },
      'pc-gf': {
        features: [
          'ガラス繊維強化PC。超高強度と寸法安定性。',
          '工業用最高グレード',
          '優れた耐熱性'
        ],
        baseResin: 'ポリカーボネート + ガラス繊維',
        derivatives: 'PCにガラス繊維を配合した最高性能フィラメント。工業用途に最適です。',
        suitableUses: ['工業部品', '自動車部品', '治具・工具', '耐熱構造物'],
        proscons: { pros: '超高強度、寸法安定性、耐熱性', cons: '印刷困難、ノズル摩耗、表面粗い、非常に高価' },
        precautions: ['硬化ノズル推奨です。', '印刷温度が非常に高く、対応プリンターが限られます。', '乾燥必須です。', '上級者向けの材料です。']
      },
      'pa': {
        features: [
          '優れた耐摩耗性と靭性',
          '高い耐薬品性',
          '柔軟性と強度の両立',
          '吸湿性が非常に高い',
          '機能部品に最適'
        ],
        baseResin: 'ポリアミド（ナイロン）',
        derivatives: 'PAをベースに、様々な特性を強化した派生フィラメントが開発されています。',
        suitableUses: ['歯車・ベアリング', '機械部品', '治具・工具', '耐摩耗部品', '可動部品'],
        proscons: { pros: '耐摩耗性、靭性、耐薬品性、柔軟性', cons: '吸湿性高い、反りやすい、乾燥必須、高価' },
        precautions: ['吸湿性が非常に高いため、使用前の乾燥が必須です。', '密閉チャンバーでの印刷を推奨します。', '印刷後も吸湿するため、密閉保管が必要です。']
      },
      'pa-cf': {
        features: [
          'カーボン繊維強化PA。高強度と耐摩耗性。',
          '工業用高性能部品',
          '優れた寸法安定性'
        ],
        baseResin: 'ポリアミド + カーボン繊維',
        derivatives: 'PAにカーボン繊維を配合した高性能フィラメント。強度と耐摩耗性を兼ね備えています。',
        suitableUses: ['高性能歯車', '工業用治具', '機械部品', '構造部品'],
        proscons: { pros: '高強度、耐摩耗性、寸法安定性', cons: 'ノズル摩耗、吸湿性、乾燥必須、非常に高価' },
        precautions: ['硬化ノズル必須です。', '使用前の乾燥が必須です。', '密閉チャンバーでの印刷を推奨します。']
      },
      'pa-gf': {
        features: [
          'ガラス繊維強化PA。高強度と寸法安定性。',
          '工業用部品',
          '優れた耐熱性'
        ],
        baseResin: 'ポリアミド + ガラス繊維',
        derivatives: 'PAにガラス繊維を配合した高性能フィラメント。寸法安定性と耐熱性に優れています。',
        suitableUses: ['工業部品', '自動車部品', '治具・工具', '耐熱部品'],
        proscons: { pros: '高強度、寸法安定性、耐熱性', cons: 'ノズル摩耗、吸湿性、表面粗い、非常に高価' },
        precautions: ['硬化ノズル推奨です。', '使用前の乾燥が必須です。', '密閉チャンバーでの印刷を推奨します。']
      },
      'tpu': {
        features: [
          '高い柔軟性と弾性',
          '優れた耐摩耗性',
          'ゴムライクな質感',
          '印刷速度に注意が必要',
          'ダイレクトドライブ推奨'
        ],
        baseResin: '熱可塑性ポリウレタン',
        derivatives: 'TPUは硬度により様々なバリエーションがあります。',
        suitableUses: ['スマホケース', 'ガスケット・パッキン', '靴底・インソール', '防振・緩衝材'],
        proscons: { pros: '柔軟性、耐摩耗性、弾性', cons: '印刷困難、低速、糸引き' },
        precautions: ['ダイレクトドライブ式エクストルーダーでの印刷を強く推奨します。', '印刷速度は15-30mm/sと低速に設定してください。', 'リトラクション設定は最小限にし、糸引きを防いでください。']
      }
    }
  };

  const TEXT_EN = {
    // 共通セクション
    features: 'Features',
    basicInfo: 'Basic Information',
    recommendedSettings: 'Recommended Settings',
    bendingStrength: 'Bending Strength',
    derivatives: 'Derivatives & Variations',
    useCases: 'Use Cases',
    precautions: 'Precautions',
    
    // 基本情報項目
    classification: 'Classification:',
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    advanced: 'Advanced',
    baseResin: 'Base Resin:',
    meltingPoint: 'Melting Point:',
    density: 'Density:',
    shrinkageRate: 'Shrinkage Rate:',
    heatResistance: 'Heat Resistance:',
    
    // 推奨設定項目
    item: 'Item',
    recommendedValue: 'Recommended Value',
    notes: 'Notes',
    nozzleTemp: 'Nozzle Temperature',
    bedTemp: 'Bed Temperature',
    fanSetting: 'Fan Setting',
    printSpeed: 'Print Speed',
    nozzleDiameter: 'Nozzle Diameter',
    caution: 'Caution',
    
    // 曲げ強度項目
    bendingStrengthValue: 'Bending Strength',
    flexuralModulus: 'Flexural Modulus',
    testMethod: 'Test Method',
    threePointBending: '3-Point Bending Test',
    details: 'Details',
    
    // 材料別の説明文
    materials: {
      'pla-cf': {
        features: [
          'Carbon fiber reinforced. Lightweight and strong. Hardened nozzle recommended.',
          'Excellent rigidity and dimensional stability',
          'Matte finish with premium look'
        ],
        baseResin: 'Polylactic Acid + Carbon Fiber',
        derivatives: 'High-strength filament based on PLA with carbon fiber. Lightweight, rigid, and suitable for functional parts.',
        suitableUses: ['Drone parts', 'Functional parts', 'Jigs & tools', 'Lightweight structures'],
        proscons: { pros: 'Lightweight, high rigidity, dimensional stability', cons: 'Nozzle wear, brittle, expensive' },
        precautions: ['Hardened nozzle (stainless steel or ruby) is required.', 'Weak layer adhesion; pay attention to stress direction.', 'More brittle than standard PLA; weak against impact.']
      },
      'pla-silk': {
        features: [
          'Glossy finish. Beautiful appearance. For decorative items.',
          'Unique glossy texture',
          'Layer lines less visible'
        ],
        baseResin: 'Polylactic Acid + Gloss Additives',
        derivatives: 'PLA with gloss additives. Beautiful finish, ideal for decorative items and art pieces.',
        suitableUses: ['Decorative items', 'Figurines', 'Art pieces', 'Gifts & souvenirs'],
        proscons: { pros: 'Beautiful gloss, wide color range, easy printing', cons: 'Low strength, not for functional parts, expensive' },
        precautions: ['Low mechanical strength; not suitable for functional parts.', 'Lower print speed improves gloss.', 'Weak layer adhesion; handle with care.']
      },
      'matte-pla': {
        features: [
          'Matte finish. Layer lines less visible, premium look.',
          'Subdued texture',
          'Fingerprints less visible'
        ],
        baseResin: 'Polylactic Acid + Matte Additives',
        derivatives: 'PLA with matte additives. Layer lines less visible with premium finish.',
        suitableUses: ['Prototypes', 'Interior items', 'Architectural models', 'Display models'],
        proscons: { pros: 'Premium look, layer lines less visible, easy printing', cons: 'Slightly lower strength, limited color options' },
        precautions: ['Slightly more brittle than standard PLA; use caution for functional parts.', 'Slightly higher print temperature improves finish.']
      },
      'pla-pro': {
        features: [
          'High strength and toughness. Suitable for functional parts.',
          'Excellent layer adhesion',
          'Improved impact resistance'
        ],
        baseResin: 'Polylactic Acid (Enhanced)',
        derivatives: 'Significantly improved mechanical properties over standard PLA. High strength and toughness for functional parts.',
        suitableUses: ['Functional parts', 'Jigs & tools', 'Snap-fit parts', 'Durable parts'],
        proscons: { pros: 'High strength, high toughness, easy printing, PLA-like ease of use', cons: 'Slightly expensive, heat resistance similar to standard PLA' },
        precautions: ['Slightly higher print temperature recommended than standard PLA.', 'Heat resistance similar to standard PLA; not suitable for high-temperature environments.']
      },
      'ht-pla': {
        features: [
          'High heat resistance PLA. Over 100°C with annealing.',
          'Heat resistance through crystallization',
          'Maintains PLA ease of use'
        ],
        baseResin: 'Polylactic Acid (High Crystallinity)',
        derivatives: 'PLA with significantly improved heat resistance through annealing (heat treatment). Can withstand over 100°C.',
        suitableUses: ['Heat-resistant parts', 'Automotive interior', 'Tableware & cutlery', 'High-temperature parts'],
        proscons: { pros: 'High heat resistance (after annealing), easy printing, food-safe', cons: 'Annealing required, significant shrinkage, expensive' },
        precautions: ['Annealing (heat treatment) is required. Heat in oven at 80-110°C for 1-2 hours.', 'Shrinks about 10-15% during annealing; consider dimensions in design.', 'Heat resistance similar to standard PLA before annealing.']
      },
      'petg-cf': {
        features: [
          'Carbon fiber reinforced PETG. High rigidity and chemical resistance.',
          'Excellent dimensional stability',
          'Matte finish'
        ],
        baseResin: 'Polyethylene Terephthalate Glycol + Carbon Fiber',
        derivatives: 'High-performance filament with carbon fiber added to PETG. Combines rigidity and chemical resistance.',
        suitableUses: ['Functional parts', 'Jigs & tools', 'Chemical-resistant parts', 'Structural parts'],
        proscons: { pros: 'High rigidity, chemical resistance, dimensional stability', cons: 'Nozzle wear, brittle, expensive' },
        precautions: ['Hardened nozzle required.', 'Weak layer adhesion; pay attention to stress direction.']
      },
      'petg-gf': {
        features: [
          'Glass fiber reinforced PETG. High strength and dimensional stability.',
          'Excellent heat resistance',
          'Ideal for industrial use'
        ],
        baseResin: 'Polyethylene Terephthalate Glycol + Glass Fiber',
        derivatives: 'High-strength filament with glass fiber added to PETG. Excellent dimensional stability and heat resistance.',
        suitableUses: ['Industrial parts', 'Jigs & tools', 'Heat-resistant parts', 'Structural parts'],
        proscons: { pros: 'High strength, dimensional stability, heat resistance', cons: 'Nozzle wear, rough surface, expensive' },
        precautions: ['Hardened nozzle recommended.', 'Surface may be rough; post-processing may be needed.']
      },
      'abs': {
        features: [
          'High impact resistance and toughness',
          'Excellent heat resistance (80-100°C)',
          'Easy to post-process',
          'Prone to warping; enclosed chamber recommended',
          'Suitable for industrial use',
          'Excellent mechanical strength'
        ],
        baseResin: 'Acrylonitrile Butadiene Styrene',
        derivatives: 'Various derivative filaments with enhanced properties have been developed based on ABS. Choose the optimal one according to your application.',
        suitableUses: ['Automotive parts', 'Electronic enclosures', 'Industrial jigs', 'Heat-resistant parts', 'Mechanical parts', 'Appliance housings'],
        proscons: { pros: 'High strength, heat resistance, post-processability, toughness', cons: 'Prone to warping, toxic fumes, high temperature required' },
        precautions: ['Toxic fumes are emitted during printing; adequate ventilation is required.', 'Prone to warping; enclosed chamber printing is strongly recommended.', 'High-temperature printing requires printer capability verification.', 'Difficult for beginners; starting with PLA+ or PETG is recommended.']
      },
      'abs-cf': {
        features: [
          'Carbon fiber reinforced ABS. Lightweight and strong.',
          'Used in aerospace applications',
          'Excellent rigidity'
        ],
        baseResin: 'Acrylonitrile Butadiene Styrene + Carbon Fiber',
        derivatives: 'High-performance filament with carbon fiber added to ABS. Lightweight and strong, used in aerospace applications.',
        suitableUses: ['Aerospace parts', 'Drone parts', 'High-performance jigs', 'Lightweight structures'],
        proscons: { pros: 'Lightweight, high rigidity, heat resistance', cons: 'Nozzle wear, prone to warping, expensive' },
        precautions: ['Hardened nozzle required.', 'Enclosed chamber printing strongly recommended.', 'Toxic fumes; ventilation required.']
      },
      'abs-gf': {
        features: [
          'Glass fiber reinforced ABS. Improved dimensional stability.',
          'For industrial parts',
          'Excellent heat resistance'
        ],
        baseResin: 'Acrylonitrile Butadiene Styrene + Glass Fiber',
        derivatives: 'High-performance filament with glass fiber added to ABS. Improved dimensional stability, ideal for industrial parts.',
        suitableUses: ['Industrial parts', 'Automotive parts', 'Jigs & tools', 'Structural parts'],
        proscons: { pros: 'Dimensional stability, heat resistance, high strength', cons: 'Nozzle wear, rough surface, expensive' },
        precautions: ['Hardened nozzle recommended.', 'Enclosed chamber printing recommended.', 'Toxic fumes; ventilation required.']
      },
      'asa': {
        features: [
          'Excellent weather resistance (UV resistance)',
          'Ideal for outdoor use',
          'Less warping than ABS',
          'High heat resistance',
          'Excellent chemical resistance'
        ],
        baseResin: 'Acrylonitrile Styrene Acrylate',
        derivatives: 'Various derivative filaments with enhanced properties have been developed based on ASA.',
        suitableUses: ['Outdoor parts', 'Automotive exterior', 'Signs & signage', 'Gardening items', 'Architectural models'],
        proscons: { pros: 'Weather resistance, UV resistance, heat resistance, less warping than ABS', cons: 'High temperature required, toxic fumes, expensive' },
        precautions: ['Toxic fumes are emitted during printing; adequate ventilation is required.', 'Enclosed chamber printing recommended.', 'High-temperature printing requires printer capability verification.']
      },
      'asa-cf': {
        features: [
          'Carbon fiber reinforced ASA. Weather resistance and high rigidity.',
          'High-performance outdoor parts',
          'Excellent dimensional stability'
        ],
        baseResin: 'Acrylonitrile Styrene Acrylate + Carbon Fiber',
        derivatives: 'High-performance filament with carbon fiber added to ASA. Combines weather resistance and rigidity.',
        suitableUses: ['Outdoor structures', 'Drone parts', 'Automotive exterior', 'High-performance jigs'],
        proscons: { pros: 'Weather resistance, high rigidity, lightweight', cons: 'Nozzle wear, expensive, toxic fumes' },
        precautions: ['Hardened nozzle required.', 'Enclosed chamber printing recommended.', 'Toxic fumes; ventilation required.']
      },
      'asa-gf': {
        features: [
          'Glass fiber reinforced ASA. Weather resistance and high strength.',
          'Industrial outdoor parts',
          'Excellent dimensional stability'
        ],
        baseResin: 'Acrylonitrile Styrene Acrylate + Glass Fiber',
        derivatives: 'High-performance filament with glass fiber added to ASA. Combines weather resistance and strength.',
        suitableUses: ['Outdoor industrial parts', 'Automotive parts', 'Building materials', 'Structural parts'],
        proscons: { pros: 'Weather resistance, high strength, dimensional stability', cons: 'Nozzle wear, rough surface, expensive' },
        precautions: ['Hardened nozzle recommended.', 'Enclosed chamber printing recommended.', 'Toxic fumes; ventilation required.']
      },
      'pc': {
        features: [
          'Extremely high impact resistance',
          'Excellent heat resistance (130-140°C)',
          'High transparency',
          'High mechanical strength',
          'High difficulty'
        ],
        baseResin: 'Polycarbonate',
        derivatives: 'Various derivative filaments with enhanced properties have been developed based on PC.',
        suitableUses: ['Protective covers', 'Safety equipment', 'Heat-resistant parts', 'Optical parts', 'Industrial jigs'],
        proscons: { pros: 'Ultra-high strength, heat resistance, transparency, impact resistance', cons: 'Difficult to print, high temperature required, hygroscopic, expensive' },
        precautions: ['Very high print temperature (270-310°C); limited compatible printers.', 'Highly hygroscopic; drying required.', 'Enclosed chamber printing strongly recommended.', 'Advanced material.']
      },
      'pc-cf': {
        features: [
          'Carbon fiber reinforced PC. Ultra-high strength.',
          'Aerospace grade',
          'Excellent heat resistance and rigidity'
        ],
        baseResin: 'Polycarbonate + Carbon Fiber',
        derivatives: 'Highest-performance filament with carbon fiber added to PC. Used in aerospace applications.',
        suitableUses: ['Aerospace parts', 'High-performance jigs', 'Heat-resistant structures', 'Protective equipment'],
        proscons: { pros: 'Ultra-high strength, heat resistance, lightweight, high rigidity', cons: 'Difficult to print, nozzle wear, very expensive' },
        precautions: ['Hardened nozzle required.', 'Very high print temperature; limited compatible printers.', 'Drying required.', 'Advanced material.']
      },
      'pc-gf': {
        features: [
          'Glass fiber reinforced PC. Ultra-high strength and dimensional stability.',
          'Industrial highest grade',
          'Excellent heat resistance'
        ],
        baseResin: 'Polycarbonate + Glass Fiber',
        derivatives: 'Highest-performance filament with glass fiber added to PC. Ideal for industrial use.',
        suitableUses: ['Industrial parts', 'Automotive parts', 'Jigs & tools', 'Heat-resistant structures'],
        proscons: { pros: 'Ultra-high strength, dimensional stability, heat resistance', cons: 'Difficult to print, nozzle wear, rough surface, very expensive' },
        precautions: ['Hardened nozzle recommended.', 'Very high print temperature; limited compatible printers.', 'Drying required.', 'Advanced material.']
      },
      'pa': {
        features: [
          'Excellent wear resistance and toughness',
          'High chemical resistance',
          'Balance of flexibility and strength',
          'Highly hygroscopic',
          'Ideal for functional parts'
        ],
        baseResin: 'Polyamide (Nylon)',
        derivatives: 'Various derivative filaments with enhanced properties have been developed based on PA.',
        suitableUses: ['Gears & bearings', 'Mechanical parts', 'Jigs & tools', 'Wear-resistant parts', 'Moving parts'],
        proscons: { pros: 'Wear resistance, toughness, chemical resistance, flexibility', cons: 'Highly hygroscopic, prone to warping, drying required, expensive' },
        precautions: ['Highly hygroscopic; drying before use is required.', 'Enclosed chamber printing recommended.', 'Absorbs moisture after printing; sealed storage required.']
      },
      'pa-cf': {
        features: [
          'Carbon fiber reinforced PA. High strength and wear resistance.',
          'Industrial high-performance parts',
          'Excellent dimensional stability'
        ],
        baseResin: 'Polyamide + Carbon Fiber',
        derivatives: 'High-performance filament with carbon fiber added to PA. Combines strength and wear resistance.',
        suitableUses: ['High-performance gears', 'Industrial jigs', 'Mechanical parts', 'Structural parts'],
        proscons: { pros: 'High strength, wear resistance, dimensional stability', cons: 'Nozzle wear, hygroscopic, drying required, very expensive' },
        precautions: ['Hardened nozzle required.', 'Drying before use is required.', 'Enclosed chamber printing recommended.']
      },
      'pa-gf': {
        features: [
          'Glass fiber reinforced PA. High strength and dimensional stability.',
          'Industrial parts',
          'Excellent heat resistance'
        ],
        baseResin: 'Polyamide + Glass Fiber',
        derivatives: 'High-performance filament with glass fiber added to PA. Excellent dimensional stability and heat resistance.',
        suitableUses: ['Industrial parts', 'Automotive parts', 'Jigs & tools', 'Heat-resistant parts'],
        proscons: { pros: 'High strength, dimensional stability, heat resistance', cons: 'Nozzle wear, hygroscopic, rough surface, very expensive' },
        precautions: ['Hardened nozzle recommended.', 'Drying before use is required.', 'Enclosed chamber printing recommended.']
      },
      'tpu': {
        features: [
          'High flexibility and elasticity',
          'Excellent wear resistance',
          'Rubber-like texture',
          'Print speed requires attention',
          'Direct drive recommended'
        ],
        baseResin: 'Thermoplastic Polyurethane',
        derivatives: 'TPU has various variations based on hardness.',
        suitableUses: ['Phone cases', 'Gaskets & seals', 'Shoe soles & insoles', 'Vibration damping & cushioning'],
        proscons: { pros: 'Flexibility, wear resistance, elasticity', cons: 'Difficult to print, slow speed, stringing' },
        precautions: ['Direct drive extruder printing is strongly recommended.', 'Set print speed to slow 15-30mm/s.', 'Minimize retraction settings to prevent stringing.']
      }
    }
  };

  const TEXT = isEnglish ? TEXT_EN : TEXT_JA;

  // 材料データを取得する関数
  const getMaterialData = (materialSlug: string) => {
    const normalized = normalizeSlug(materialSlug);
    const finalKey = SLUG_ALIAS_MAP[normalized] || normalized;
    return TEXT.materials[finalKey as keyof typeof TEXT.materials] || null;
  };

  return {
    ...TEXT,
    isEnglish,
    getMaterialData,
    normalizedSlug
  };
};
